import { api } from '../api';

const AUTHORIZATION_VALUE_PREFIX = 'mfasession';

export enum MultiFactorAuthenticationType {
  Email = 'email',
  SMS = 'sms',
  TOTP = 'totp',
}

export interface MultiFactorAuthenticationProviderOption<T = MultiFactorAuthenticationType> {
  name: T;
}

interface EmailMultiFactorAuthenticationProviderOption
  extends MultiFactorAuthenticationProviderOption<MultiFactorAuthenticationType.Email> {
  email: string;
}

interface SMSMultiFactorAuthenticationProviderOption
  extends MultiFactorAuthenticationProviderOption<MultiFactorAuthenticationType.SMS> {
  allowed_countries: { country: string; prefix: string }[];
}

export function isEmailMultiFactorAuthenticationProviderOption(
  option: MultiFactorAuthenticationProviderOption,
): option is EmailMultiFactorAuthenticationProviderOption {
  return option.name === MultiFactorAuthenticationType.Email;
}

export function isSMSMultiFactorAuthenticationProviderOption(
  option: MultiFactorAuthenticationProviderOption,
): option is SMSMultiFactorAuthenticationProviderOption {
  return option.name === MultiFactorAuthenticationType.SMS;
}

export type MultiFactorAuthentication = {
  id: string;
  provider: MultiFactorAuthenticationType;
};

export type EmailMultiFactorAuthentication = MultiFactorAuthentication & {
  email: string;
};

export function isEmailMultiFactorAuthentication(
  authentication: MultiFactorAuthentication,
): authentication is EmailMultiFactorAuthentication {
  return authentication.provider === MultiFactorAuthenticationType.Email;
}

export type SmsMultiFactorAuthentication = MultiFactorAuthentication & {
  number: string;
};

export function isSmsMultiFactorAuthentication(
  authentication: MultiFactorAuthentication,
): authentication is SmsMultiFactorAuthentication {
  return authentication.provider === MultiFactorAuthenticationType.SMS;
}

export enum EnforcementStatus {
  Enforced = 'enforced',
  Deactivated = 'deactivated',
  PendingEnforcement = 'pending_enforcement',
}

type MultiFactorAuthenticationListResponse = {
  data: Array<MultiFactorAuthentication>;
  meta: {
    default_id: string;
    enforcement_status: EnforcementStatus;
    provider_options: MultiFactorAuthenticationProviderOption[];
  };
};

const list = async (token: string) =>
  api.post<MultiFactorAuthenticationListResponse>(
    '/multiFactorAuthentication.list',
    {},
    {
      Authorization: `${AUTHORIZATION_VALUE_PREFIX} ${token}`,
    },
  );

export default list;
